import type { ComponentProps } from 'react';

import { cn } from '../lib/cn';

export type TableProps = ComponentProps<'table'> & {
  /**
   * Nome acessível do contêiner rolável: com ele, o contêiner vira uma região focável por teclado (`role="region"`,
   * `aria-label`, `tabIndex=0`). Obrigatório em tabelas que podem ficar mais largas que a viewport (muitas colunas,
   * identificadores): a WCAG 2.1.1 exige que a região que rola na horizontal seja alcançável pelo teclado (axe
   * `scrollable-region-focusable` — sério; no Linux do CI as fontes são mais largas e a tabela transborda antes).
   */
  readonly regionLabel?: string | undefined;
};

export function Table({ className, regionLabel, ...props }: TableProps) {
  const isSrOnly = typeof className === 'string' && className.split(/\s+/).includes('sr-only');
  return (
    <div
      data-slot="table-container"
      className={cn(
        'relative w-full rounded-control focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring',
        isSrOnly ? 'overflow-visible' : 'overflow-x-auto',
      )}
      // Visible scroll regions must accept keyboard focus.
      tabIndex={isSrOnly ? undefined : 0}
      {...(regionLabel ? { role: 'region', 'aria-label': regionLabel } : {})}
    >
      <table
        data-slot="table"
        className={cn('w-full caption-bottom text-body', className)}
        {...props}
      />
    </div>
  );
}

export function TableHeader({ className, ...props }: ComponentProps<'thead'>) {
  return (
    <thead
      data-slot="table-header"
      className={cn('bg-surface-sunken [&_tr]:border-b [&_tr]:border-border', className)}
      {...props}
    />
  );
}

export function TableBody({ className, ...props }: ComponentProps<'tbody'>) {
  return (
    <tbody
      data-slot="table-body"
      className={cn('[&_tr:last-child]:border-0', className)}
      {...props}
    />
  );
}

export function TableFooter({ className, ...props }: ComponentProps<'tfoot'>) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        'border-t border-border bg-surface-sunken font-medium [&>tr]:last:border-b-0',
        className,
      )}
      {...props}
    />
  );
}

export function TableRow({ className, ...props }: ComponentProps<'tr'>) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        'h-(--table-row-h) border-b border-border transition-colors hover:bg-surface-sunken [tbody_&]:hover:bg-accent data-[state=selected]:bg-accent',
        className,
      )}
      {...props}
    />
  );
}

export function TableHead({ className, ...props }: ComponentProps<'th'>) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        'h-(--table-row-h) px-(--table-cell-px) text-left align-middle text-caption font-semibold tracking-wide whitespace-nowrap text-muted-foreground uppercase [&:has([role=checkbox])]:pr-0',
        className,
      )}
      {...props}
    />
  );
}

export function TableCell({ className, ...props }: ComponentProps<'td'>) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        'px-(--table-cell-px) py-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0',
        className,
      )}
      {...props}
    />
  );
}

export function TableCaption({ className, ...props }: ComponentProps<'caption'>) {
  return (
    <caption
      data-slot="table-caption"
      className={cn('mt-4 text-body-sm text-muted-foreground', className)}
      {...props}
    />
  );
}
